import * as turf from "@turf/turf";
import type { MapObject } from "./MapObject";
import { MapLabelModel } from "./MapLabel.model";

export class MapLabel extends MapLabelModel {
    static layerId(mapObject: MapObject): string {
        return `${mapObject.uuid}_label`;
    }

    static leaderLayerId(mapObject: MapObject): string {
        return `${mapObject.uuid}_label_leader`;
    }

    // Every layer addLeaderLine and addLabel draw for an item, so visibility toggles and layer cleanup cover them all.
    static layerIds(mapObject: MapObject): string[] {
        return [
            `${MapLabel.leaderLayerId(mapObject)}_casing`,
            MapLabel.leaderLayerId(mapObject),
            MapLabel.layerId(mapObject),
        ];
    }

    // Falls back to the mappable's name so renaming the item keeps its label in sync.
    displayText(mapObject: MapObject): string {
        return this.text?.trim() || mapObject.mappableName || "";
    }

    // Point and custom labels are drawn from a single point feature; line labels follow the item's own geometry.
    mapboxSymbolPlacement(): "point" | "line" | "line-center" {
        switch (this.placement) {
            case "line":
                return "line";
            case "line_center":
                return "line-center";
            default:
                return "point";
        }
    }

    mapboxTextAnchor(): mapboxgl.Anchor {
        return (this.anchor || "center").replace("_", "-") as mapboxgl.Anchor;
    }

    // Where a custom label's leader line meets its item: the point itself, the closest spot on a line, or the closest
    // spot on a polygon's outline. A label dropped inside its polygon already sits on it, so it gets no leader.
    leaderTarget(mapObject: MapObject): [number, number] | null {
        if (
            this.placement !== "custom" ||
            this.showLeaderLine === false ||
            !this.coordinates ||
            !mapObject.coordinates
        ) {
            return null;
        }

        const labelPoint = turf.point(this.coordinates);
        try {
            switch (mapObject.geometry) {
                case "point":
                    return mapObject.coordinates;
                case "lineString":
                    return turf.nearestPointOnLine(turf.lineString(mapObject.coordinates), labelPoint).geometry
                        .coordinates as [number, number];
                case "polygon": {
                    const polygon = turf.polygon(mapObject.coordinates);
                    if (turf.booleanPointInPolygon(labelPoint, polygon)) {
                        return null;
                    }
                    const outline = turf.polygonToLine(polygon) as GeoJSON.Feature<
                        GeoJSON.LineString | GeoJSON.MultiLineString
                    >;
                    return turf.nearestPointOnLine(outline, labelPoint).geometry.coordinates as [number, number];
                }
                default:
                    return null;
            }
        } catch {
            // Mid-draw geometry can be incomplete (eg. a one-point line), skip the leader until it's valid.
            return null;
        }
    }

    toFeatures(mapObject: MapObject): GeoJSON.Feature[] {
        const text = this.displayText(mapObject);
        if (this._destroy || !text) {
            return [];
        }

        const followsGeometry =
            (this.placement === "line" || this.placement === "line_center") && mapObject.geometry !== "point";

        const geometry: GeoJSON.Geometry = followsGeometry
            ? ({ type: mapObject.mapBoxGeometry(), coordinates: mapObject.coordinates } as GeoJSON.Geometry)
            : {
                  type: "Point",
                  coordinates:
                      this.placement === "custom" && this.coordinates
                          ? this.coordinates
                          : (mapObject.getCenter() as [number, number]),
              };

        const features: GeoJSON.Feature[] = [
            {
                id: MapLabel.layerId(mapObject),
                type: "Feature",
                properties: {
                    // uuid lets a click on the label select its item, same as clicking the item itself.
                    uuid: mapObject.uuid,
                    id: MapLabel.layerId(mapObject),
                    labelText: text,
                    labelAnchor: this.mapboxTextAnchor(),
                },
                geometry,
            },
        ];

        const leaderTarget = this.leaderTarget(mapObject);
        if (leaderTarget) {
            features.push({
                id: MapLabel.leaderLayerId(mapObject),
                type: "Feature",
                properties: { uuid: mapObject.uuid, id: MapLabel.leaderLayerId(mapObject) },
                geometry: { type: "LineString", coordinates: [this.coordinates, leaderTarget] },
            });
        }

        return features;
    }
}
